import { ApolloQueryResult, useQuery, useReactiveVar } from '@apollo/client';
import FeatureFlagsContext from 'context/FeatureFlagsContext';
import { GET_UNIFIED_PERMITS } from 'graphql/queries/getUnifiedPermits';
import { READ_BENEFITS } from 'graphql/queries/readBenefits';
import { useContext, useMemo } from 'react';
import { featureCountryCode } from 'states/persistInStorage';
import { useAuthStore } from 'stores/authStore';
import {
  CompanyBenefit,
  CountryCode,
  Query,
  UnifiedPermit,
} from 'types/generatedSchemaTypes';
import { parseFeatureFlagObject, TypeDictionary } from 'utils/featureFlagUtils';

interface UsePermitsAndBenefitsProps {
  customerUid?: string;
}

interface UsePermitsAndBenefitsReturn {
  permits: UnifiedPermit[] | undefined;
  benefits: CompanyBenefit[] | undefined;
  loading: boolean;
  error: any;
  refetchPermits: () => Promise<ApolloQueryResult<Query>>;
  refetchBenefits: () => Promise<ApolloQueryResult<Query>>;
}

export const usePermitsAndBenefits = ({
  customerUid,
}: UsePermitsAndBenefitsProps = {}): UsePermitsAndBenefitsReturn => {
  const token = useAuthStore((state) => state.accessToken);
  const appCountry = useReactiveVar(featureCountryCode);
  const {
    flags: { sales_order_permits },
  } = useContext(FeatureFlagsContext);

  const {
    data: { readUnifyPermits: permits } = {},
    loading: permitsLoading,
    error: permitsError,
    refetch: refetchPermits,
  } = useQuery<Query>(GET_UNIFIED_PERMITS, {
    fetchPolicy: 'cache-and-network',
    skip: !token || !customerUid,
    errorPolicy: 'all',
  });

  const {
    data: { readBenefits: benefits } = {},
    loading: benefitsLoading,
    error: benefitsError,
    refetch: refetchBenefits,
  } = useQuery<Query>(READ_BENEFITS, {
    fetchPolicy: 'cache-and-network',
    skip: !token || !customerUid,
    errorPolicy: 'all',
  });

  const filteredPermits = useMemo(() => {
    if (!permits) {
      return undefined;
    }

    const countryObject = parseFeatureFlagObject<
      TypeDictionary<CountryCode, boolean>
    >(sales_order_permits, 'sales_order_permits');

    if (appCountry && countryObject[appCountry]) {
      return permits.filter(Boolean) as UnifiedPermit[];
    }

    return permits
      .filter(
        (permit) =>
          permit?.permitType === 'ACCESS' || permit?.permitType === 'LOYALTY'
      )
      .filter(Boolean) as UnifiedPermit[];
  }, [permits, appCountry, sales_order_permits]);

  const filteredBenefits = useMemo(() => {
    if (!benefits) {
      return undefined;
    }
    return benefits.filter(Boolean) as CompanyBenefit[];
  }, [benefits]);

  return {
    permits: filteredPermits,
    benefits: filteredBenefits,
    loading: permitsLoading || benefitsLoading,
    error: permitsError || benefitsError,
    refetchPermits,
    refetchBenefits,
  };
};
